/**
 * useAffiliateTracking Hook
 * ==========================
 * Handles affiliate referral tracking on the frontend.
 * 
 * Features:
 * - Captures ?ref=CODE from URL on landing
 * - Stores affiliate code in cookie (90 days) and localStorage
 * - Validates affiliate code with backend
 * - Provides attribution data for signup flow
 * 
 * Usage:
 * ```
 * const { affiliateCode, affiliateValid, trackAttribution } = useAffiliateTracking();
 * 
 * // On signup success:
 * await trackAttribution(userId, userEmail, orgId);
 * ```
 */
import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';

// Cookie helpers
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

// LocalStorage helpers
const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('localStorage unavailable:', e);
  }
};

const getLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (e) {
    console.warn('localStorage unavailable:', e);
    return null;
  }
};

const AFFILIATE_COOKIE_NAME = 'mnm_affiliate';
const AFFILIATE_STORAGE_KEY = 'mnm_affiliate_data';
const COOKIE_EXPIRY_DAYS = 90;

export const useAffiliateTracking = () => {
  const [affiliateCode, setAffiliateCode] = useState(null);
  const [affiliateValid, setAffiliateValid] = useState(false);
  const [affiliateData, setAffiliateData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validate affiliate code with backend
  const validateCode = useCallback(async (code) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/affiliate/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      
      const data = await response.json();
      return data.valid === true;
    } catch (error) {
      console.warn('Failed to validate affiliate code:', error);
      return false;
    }
  }, []);

  // Store affiliate data
  const storeAffiliateData = useCallback((code, source = 'url') => {
    const data = {
      code,
      first_visit_at: new Date().toISOString(),
      landing_page: window.location.pathname,
      referral_url: window.location.href,
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
      source
    };
    
    // Store in cookie (90 days)
    setCookie(AFFILIATE_COOKIE_NAME, code, COOKIE_EXPIRY_DAYS);
    
    // Store full data in localStorage
    setLocalStorage(AFFILIATE_STORAGE_KEY, data);
    
    setAffiliateCode(code);
    setAffiliateData(data);
    setAffiliateValid(true);
    
    console.log('[AFFILIATE] Stored affiliate data:', code);
  }, []);

  // Initialize - check URL params and existing storage
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      
      // Check URL for ref parameter
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      
      if (refCode) {
        // New referral - validate and store
        const isValid = await validateCode(refCode);
        
        if (isValid) {
          storeAffiliateData(refCode, 'url');
        } else {
          console.log('[AFFILIATE] Invalid affiliate code:', refCode);
        }
      } else {
        // Check existing storage
        const existingCode = getCookie(AFFILIATE_COOKIE_NAME);
        const existingData = getLocalStorage(AFFILIATE_STORAGE_KEY);
        
        if (existingCode) {
          // Re-validate existing code
          const isValid = await validateCode(existingCode);
          
          if (isValid) {
            setAffiliateCode(existingCode);
            setAffiliateData(existingData);
            setAffiliateValid(true);
          } else {
            // Code no longer valid - clear storage
            clearAffiliateData();
          }
        }
      }
      
      setLoading(false);
    };
    
    init();
  }, [validateCode, storeAffiliateData]);

  // Clear affiliate data
  const clearAffiliateData = useCallback(() => {
    deleteCookie(AFFILIATE_COOKIE_NAME);
    localStorage.removeItem(AFFILIATE_STORAGE_KEY);
    setAffiliateCode(null);
    setAffiliateData(null);
    setAffiliateValid(false);
  }, []);

  // Track attribution after signup
  const trackAttribution = useCallback(async (userId, userEmail = null, orgId = null) => {
    if (!affiliateCode || !affiliateValid) {
      console.log('[AFFILIATE] No valid affiliate code to attribute');
      return { success: true, attributed: false };
    }
    
    try {
      const storedData = getLocalStorage(AFFILIATE_STORAGE_KEY) || {};
      
      const response = await fetch(`${BACKEND_URL}/api/affiliate/track-attribution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          affiliate_code: affiliateCode,
          org_id: orgId,
          user_email: userEmail,
          referral_url: storedData.referral_url,
          utm_source: storedData.utm_source,
          utm_medium: storedData.utm_medium,
          utm_campaign: storedData.utm_campaign,
          landing_page: storedData.landing_page,
          first_visit_at: storedData.first_visit_at
        })
      });
      
      const data = await response.json();
      
      if (data.attributed) {
        console.log('[AFFILIATE] Attribution tracked successfully:', data.attribution_id);
        // Don't clear the cookie - it's still needed for Stripe metadata
      }
      
      return data;
    } catch (error) {
      console.error('[AFFILIATE] Failed to track attribution:', error);
      return { success: true, attributed: false, error: error.message };
    }
  }, [affiliateCode, affiliateValid]);

  // Get affiliate data for Stripe metadata
  const getAffiliateMetadata = useCallback(() => {
    if (!affiliateCode || !affiliateValid) {
      return null;
    }
    
    return {
      affiliate_code: affiliateCode
    };
  }, [affiliateCode, affiliateValid]);

  return {
    affiliateCode,
    affiliateValid,
    affiliateData,
    loading,
    trackAttribution,
    getAffiliateMetadata,
    clearAffiliateData
  };
};

export default useAffiliateTracking;
